import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Calendar } from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { format, subDays, subMonths, startOfDay, eachDayOfInterval, eachWeekOfInterval, startOfWeek, endOfWeek } from 'date-fns';
import { cn } from '@/lib/utils';
import { MOOD_OPTIONS, type MoodEntry } from '@/types/mood';
import { useLanguage } from '@/i18n/LanguageContext';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';

type ViewMode = 'weekly' | 'monthly';

interface MoodTrendChartProps {
  entries: MoodEntry[];
  isLoading?: boolean;
}

interface ChartDataPoint {
  date: string;
  dateLabel: string;
  mood: number | null;
  moodLabel: string;
  emoji: string;
}

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length > 0) {
    const data = payload[0].payload as ChartDataPoint;
    if (data.mood === null) {
      return (
        <div className="bg-popover border border-border rounded-lg px-3 py-2 shadow-lg">
          <p className="text-sm text-muted-foreground">{data.dateLabel}</p>
          <p className="text-xs text-muted-foreground/70">No entry</p>
        </div>
      );
    }
    const moodOption = MOOD_OPTIONS.find((m) => m.value === data.mood);
    return (
      <div className="bg-popover border border-border rounded-lg px-3 py-2 shadow-lg">
        <p className="text-sm text-muted-foreground">{data.dateLabel}</p>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-lg">{data.emoji}</span>
          <span className={cn('font-medium', moodOption?.color)}>{data.moodLabel}</span>
        </div>
      </div>
    );
  }
  return null;
};

export function MoodTrendChart({ entries, isLoading }: MoodTrendChartProps) {
  const { language } = useLanguage();
  const [viewMode, setViewMode] = useState<ViewMode>('weekly');

  const content = {
    en: {
      weeklyTitle: 'Weekly Mood Trend',
      monthlyTitle: 'Monthly Mood Trend',
      weeklySubtitle: 'Your emotional journey this week',
      monthlySubtitle: 'Your emotional journey this month',
      noData: 'Start tracking your mood to see trends',
      average: 'Avg',
      weekly: 'Week',
      monthly: 'Month',
    },
    es: {
      weeklyTitle: 'Tendencia Semanal',
      monthlyTitle: 'Tendencia Mensual',
      weeklySubtitle: 'Tu viaje emocional esta semana',
      monthlySubtitle: 'Tu viaje emocional este mes',
      noData: 'Comienza a registrar tu estado para ver tendencias',
      average: 'Prom',
      weekly: 'Semana',
      monthly: 'Mes',
    },
  };

  const t = content[language as keyof typeof content] || content.en;

  const chartData = useMemo(() => {
    const today = startOfDay(new Date());

    if (viewMode === 'weekly') {
      // Last 7 days
      const weekAgo = subDays(today, 6);
      const days = eachDayOfInterval({ start: weekAgo, end: today });

      return days.map((day) => {
        const dayStr = format(day, 'yyyy-MM-dd');
        
        const dayEntries = entries.filter((entry) => {
          const entryDate = format(new Date(entry.created_at), 'yyyy-MM-dd');
          return entryDate === dayStr;
        });

        const latestEntry = dayEntries.length > 0
          ? dayEntries.reduce((latest, entry) =>
              new Date(entry.created_at) > new Date(latest.created_at) ? entry : latest
            )
          : null;

        const moodOption = latestEntry
          ? MOOD_OPTIONS.find((m) => m.value === latestEntry.mood_value)
          : null;

        return {
          date: format(day, 'EEE'),
          dateLabel: format(day, 'EEEE, MMM d'),
          mood: latestEntry?.mood_value || null,
          moodLabel: moodOption?.label || '',
          emoji: moodOption?.emoji || '',
        } as ChartDataPoint;
      });
    } else {
      // Last 4 weeks (monthly view)
      const monthAgo = subMonths(today, 1);
      const weeks = eachWeekOfInterval(
        { start: monthAgo, end: today },
        { weekStartsOn: 0 }
      );

      return weeks.map((weekStart) => {
        const weekEnd = endOfWeek(weekStart, { weekStartsOn: 0 });
        const actualEnd = weekEnd > today ? today : weekEnd;

        // Get all entries for this week
        const weekEntries = entries.filter((entry) => {
          const entryDate = new Date(entry.created_at);
          return entryDate >= weekStart && entryDate <= actualEnd;
        });

        // Calculate average mood for the week
        if (weekEntries.length === 0) {
          return {
            date: format(weekStart, 'MMM d'),
            dateLabel: `Week of ${format(weekStart, 'MMM d')} - ${format(actualEnd, 'MMM d')}`,
            mood: null,
            moodLabel: '',
            emoji: '',
          } as ChartDataPoint;
        }

        const avgMood = Math.round(
          weekEntries.reduce((sum, e) => sum + e.mood_value, 0) / weekEntries.length
        );
        const moodOption = MOOD_OPTIONS.find((m) => m.value === avgMood);

        return {
          date: format(weekStart, 'MMM d'),
          dateLabel: `Week of ${format(weekStart, 'MMM d')} - ${format(actualEnd, 'MMM d')}`,
          mood: avgMood,
          moodLabel: moodOption?.label || '',
          emoji: moodOption?.emoji || '',
        } as ChartDataPoint;
      });
    }
  }, [entries, viewMode]);

  const averageMood = useMemo(() => {
    const validMoods = chartData.filter((d) => d.mood !== null);
    if (validMoods.length === 0) return null;
    const sum = validMoods.reduce((acc, d) => acc + (d.mood || 0), 0);
    return (sum / validMoods.length).toFixed(1);
  }, [chartData]);

  const hasAnyData = chartData.some((d) => d.mood !== null);

  if (isLoading) {
    return (
      <div className="rounded-2xl border border-border/50 bg-card p-6 h-[320px] animate-pulse">
        <div className="h-4 w-32 bg-muted rounded mb-2" />
        <div className="h-3 w-48 bg-muted/50 rounded mb-6" />
        <div className="h-[200px] bg-muted/30 rounded" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="rounded-2xl border border-border/50 bg-card p-6"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-primary" />
          <div>
            <h3 className="font-semibold text-foreground">
              {viewMode === 'weekly' ? t.weeklyTitle : t.monthlyTitle}
            </h3>
            <p className="text-sm text-muted-foreground">
              {viewMode === 'weekly' ? t.weeklySubtitle : t.monthlySubtitle}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {/* View Mode Toggle */}
          <ToggleGroup
            type="single"
            value={viewMode}
            onValueChange={(value) => value && setViewMode(value as ViewMode)}
            className="bg-muted/50 p-1 rounded-lg"
          >
            <ToggleGroupItem
              value="weekly"
              className="text-xs px-3 py-1.5 data-[state=on]:bg-background data-[state=on]:shadow-sm"
            >
              {t.weekly}
            </ToggleGroupItem>
            <ToggleGroupItem
              value="monthly"
              className="text-xs px-3 py-1.5 data-[state=on]:bg-background data-[state=on]:shadow-sm"
            >
              {t.monthly}
            </ToggleGroupItem>
          </ToggleGroup>

          {averageMood && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10">
              <Calendar className="h-3.5 w-3.5 text-primary" />
              <span className="text-sm font-medium text-primary">
                {t.average}: {averageMood}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Chart */}
      {hasAnyData ? (
        <div className="h-[200px] mt-4">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={chartData}
              margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
            >
              <defs>
                <linearGradient id="moodGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="hsl(var(--border))"
                opacity={0.5}
                vertical={false}
              />
              {/* @ts-ignore recharts type compat */}
              <XAxis
                dataKey="date"
                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                tickLine={false}
                axisLine={false}
              />
              {/* @ts-ignore recharts type compat */}
              <YAxis
                domain={[1, 5]}
                ticks={[1, 2, 3, 4, 5]}
                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => {
                  const mood = MOOD_OPTIONS.find((m) => m.value === value);
                  return mood?.emoji || '';
                }}
              />
              {/* @ts-ignore recharts type compat */}
              <Tooltip content={<CustomTooltip />} />
              {averageMood && (
                // @ts-ignore recharts type compat
                <ReferenceLine
                  y={parseFloat(averageMood)}
                  stroke="hsl(var(--primary))"
                  strokeDasharray="5 5"
                  strokeOpacity={0.5}
                />
              )}
              {/* @ts-ignore recharts type compat */}
              <Area
                type="monotone"
                dataKey="mood"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                fill="url(#moodGradient)"
                connectNulls={false}
                dot={(props) => {
                  if (props.payload.mood === null) return null;
                  return (
                    <circle
                      cx={props.cx}
                      cy={props.cy}
                      r={6}
                      fill="hsl(var(--background))"
                      stroke="hsl(var(--primary))"
                      strokeWidth={2}
                    />
                  );
                }}
                activeDot={{
                  r: 8,
                  fill: 'hsl(var(--primary))',
                  stroke: 'hsl(var(--background))',
                  strokeWidth: 2,
                }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="h-[200px] flex items-center justify-center text-muted-foreground">
          <div className="text-center">
            <div className="text-4xl mb-2">📊</div>
            <p className="text-sm">{t.noData}</p>
          </div>
        </div>
      )}

      {/* Mood Legend */}
      <div className="flex justify-center gap-3 mt-4 flex-wrap">
        {MOOD_OPTIONS.map((mood) => (
          <div key={mood.value} className="flex items-center gap-1 text-xs text-muted-foreground">
            <span>{mood.emoji}</span>
            <span>{mood.value}</span>
          </div>
        ))}
      </div>
    </motion.div>
  );
}
